import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../context/ToastContext';
import PrimaryActionButton from '../common/PrimaryActionButton';
import SwitchField from '../common/SwitchField';
import WeeklyHoursEditor from '../common/WeeklyHoursEditor';
import TeamMemberService from '../../services/teamMemberService';
import {
  PERIOD_ERROR,
  buildFollowingPeriod,
  getWeekErrorCode,
  periodsByDay,
  weekFromApi,
  weekToApi,
} from '../../utils/weeklyHoursUtils';

const DEFAULT_PERIOD = { from: '09:00', to: '18:00' };

const ERROR_MESSAGE_KEYS = {
  [PERIOD_ERROR.INVALID_PERIOD]: 'storeRegistration.validation.openTimeAfterCloseTime',
  [PERIOD_ERROR.OVERLAP_PERIODS]: 'teamMembers.workingHours.validation.overlapPeriods',
  [PERIOD_ERROR.OVERLAP_REFERENCE]: 'teamMembers.workingHours.validation.overlapPeriods',
};

const buildInitialWeek = (workingHours, storeOpeningHours) => weekFromApi(
  workingHours || storeOpeningHours,
  DEFAULT_PERIOD,
);

const TeamMemberWorkingHoursPanel = ({
  storeId,
  teamMemberId,
  workingHours,
  storeOpeningHours,
  canUpdate,
  onSaved,
}) => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useToast();
  const [usesStoreHours, setUsesStoreHours] = useState(!workingHours);
  const [week, setWeek] = useState(() => buildInitialWeek(workingHours, storeOpeningHours));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setUsesStoreHours(!workingHours);
    setWeek(buildInitialWeek(workingHours, storeOpeningHours));
  }, [workingHours, storeOpeningHours]);

  const storeHoursByDay = useMemo(() => periodsByDay(storeOpeningHours), [storeOpeningHours]);

  const showValidationError = (errorCode) => showError(t(ERROR_MESSAGE_KEYS[errorCode]));

  const handleSave = async () => {
    if (!usesStoreHours) {
      const errorCode = getWeekErrorCode(week);
      if (errorCode) {
        showValidationError(errorCode);
        return;
      }
    }

    try {
      setSaving(true);
      const saved = await TeamMemberService.updateTeamMemberWorkingHours(
        teamMemberId,
        storeId,
        usesStoreHours ? null : weekToApi(week),
      );
      showSuccess(t('teamMembers.workingHours.success.saved'));
      onSaved?.(saved?.workingHours ?? null);
    } catch (error) {
      showError(error?.response?.data?.message || t('teamMembers.workingHours.error.save'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      <SwitchField
        checked={usesStoreHours}
        onChange={(event) => setUsesStoreHours(event.target.checked)}
        disabled={!canUpdate}
        label={t('teamMembers.workingHours.useStoreHours')}
        containerSx={{ mb: 2 }}
      />

      {usesStoreHours ? null : (
        <WeeklyHoursEditor
          value={week}
          onChange={setWeek}
          canUpdate={canUpdate}
          referencePeriodsByDay={storeHoursByDay}
          referenceLabel={t('teamMembers.workingHours.storeHoursLabel')}
          nextPeriodFor={buildFollowingPeriod}
          applyAllFieldLabel={t('teamMembers.workingHours.title')}
          onValidationError={showValidationError}
        />
      )}

      {canUpdate && (
        <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
          <PrimaryActionButton
            onClick={handleSave}
            disabled={saving}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {saving ? t('common.saving') : t('teamMembers.workingHours.save')}
          </PrimaryActionButton>
        </Box>
      )}
    </Box>
  );
};

export default TeamMemberWorkingHoursPanel;
